import React, { useState, useEffect, useMemo } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaDownload, FaTag, FaCalendar, FaChartLine, FaSpinner, FaTimes, FaCheckCircle, FaFilter } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { campaignService } from '../../../../services/campaignService';

const Marketing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discount: '',
    startDate: '',
    endDate: '',
    status: 'Active'
  });

  // Load campaigns from API on mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await campaignService.getAllCampaigns({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined
      });
      
      // Transform API data to match component format
      const transformedCampaigns = data.map(campaign => ({
        id: campaign._id || campaign.id,
        name: campaign.name,
        code: campaign.code,
        discount: campaign.discount,
        discountValue: campaign.discountValue,
        status: campaign.status,
        startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
        endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
        usage: campaign.usage || 0,
        revenue: campaign.revenue || 0
      }));
      
      setCampaigns(transformedCampaigns);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      toast.error(error.message || 'Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Load campaign stats from API
  const [campaignStats, setCampaignStats] = useState({
    active: 0,
    totalUsage: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const stats = await campaignService.getCampaignStats({
          status: statusFilter !== 'all' ? statusFilter : undefined
        });
        setCampaignStats({
          active: stats.active || 0,
          totalUsage: stats.totalUsage || 0,
          totalRevenue: stats.totalRevenue || 0
        });
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    };
    loadStats();
  }, [statusFilter]);

  // Reload campaigns when filters change
  useEffect(() => {
    loadCampaigns();
  }, [searchTerm, statusFilter]);

  const filteredCampaigns = campaigns;

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `₹${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const handleCreateCampaign = () => {
    setFormData({
      name: '',
      code: '',
      discount: '',
      startDate: '',
      endDate: '',
      status: 'Active'
    });
    setSelectedCampaign(null);
    setShowCampaignModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name || '',
      code: campaign.code || '',
      discount: campaign.discountValue?.toString() || campaign.discount?.replace('%', '') || '',
      startDate: campaign.startDate || '',
      endDate: campaign.endDate || '',
      status: campaign.status || 'Active'
    });
    setShowEditModal(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }
    
    try {
      setLoading(true);
      await campaignService.deleteCampaign(campaignId);
      toast.success('Campaign deleted successfully!');
      await loadCampaigns(); // Reload campaigns
    } catch (error) {
      toast.error(error.message || 'Failed to delete campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code || !formData.discount || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const discountValue = parseInt(formData.discount);
    if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
      toast.error('Discount must be between 0 and 100');
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    try {
      setLoading(true);
      
      if (showCampaignModal) {
        // Create new campaign
        await campaignService.createCampaign({
          name: formData.name,
          code: formData.code.toUpperCase(),
          discountValue: discountValue,
          status: formData.status,
          startDate: formData.startDate,
          endDate: formData.endDate
        });
        toast.success('Campaign created successfully!');
        setShowCampaignModal(false);
      } else if (showEditModal && selectedCampaign) {
        // Update existing campaign
        await campaignService.updateCampaign(selectedCampaign.id, {
          name: formData.name,
          code: formData.code.toUpperCase(),
          discountValue: discountValue,
          status: formData.status,
          startDate: formData.startDate,
          endDate: formData.endDate
        });
        toast.success('Campaign updated successfully!');
        setShowEditModal(false);
        setSelectedCampaign(null);
      }
      
      setFormData({
        name: '',
        code: '',
        discount: '',
        startDate: '',
        endDate: '',
        status: 'Active'
      });
      
      await loadCampaigns(); // Reload campaigns
    } catch (error) {
      toast.error(error.message || 'Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAnalytics = async (campaign) => {
    try {
      setLoading(true);
      const analytics = await campaignService.getCampaignAnalytics(campaign.id);
      setSelectedCampaign({
        ...campaign,
        analytics
      });
      setShowAnalyticsModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const csvData = await campaignService.exportCampaigns(null, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined
      });
      
      // Create and download file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `marketing_campaigns_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Campaigns exported successfully!');
    } catch (error) {
      console.error('Failed to export campaigns:', error);
      toast.error(error.message || 'Failed to export campaigns');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Marketing & Promotion Management</h2>
          <p className="text-xs text-gray-600 mt-1">Create and manage promotional campaigns</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={exporting || campaigns.length === 0}
            className="bg-white border border-red-500 text-red-500 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors"
            title="Export campaigns to CSV"
          >
            {exporting ? <FaSpinner className="animate-spin text-red-500" /> : <FaDownload className="text-red-500" />} <span className="text-red-500">Export</span>
          </button>
          <button 
            onClick={handleCreateCampaign}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <FaPlus /> Create Campaign
          </button>
        </div>
      </div>

      {/* Summary Cards - Real-time Data */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-6 border border-green-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-700 mb-1">Active Campaigns</p>
              <p className="text-2xl font-bold text-green-900">
                {campaignStats.active.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FaCheckCircle className="text-xl text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-1">Total Usage</p>
              <p className="text-2xl font-bold text-blue-900">
                {campaignStats.totalUsage.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FaTag className="text-xl text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-purple-700 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatCurrency(campaignStats.totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FaChartLine className="text-xl text-purple-600" />
        </div>
        </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          </div>
          <div className="relative">
            <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Expired">Expired</option>
              <option value="Scheduled">Scheduled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Loading campaigns...</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No campaigns found. Create your first campaign to get started.
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white">
                        <FaTag />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{campaign.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                      {campaign.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-green-600">{campaign.discount}</td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {campaign.startDate} to {campaign.endDate}
                  </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{campaign.usage?.toLocaleString() || 0}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatCurrency(campaign.revenue || 0)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      campaign.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleViewAnalytics(campaign)}
                            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition" 
                            title="View Analytics"
                          >
                        <FaChartLine />
                      </button>
                          <button 
                            onClick={() => handleEditCampaign(campaign)}
                            className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-lg transition" 
                            title="Edit"
                          >
                        <FaEdit />
                      </button>
                          <button 
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition" 
                            title="Delete"
                          >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
                  ))
                )}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Create New Campaign</h3>
              <button
                onClick={() => setShowCampaignModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSaveCampaign} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Summer Sale 2024"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Code *</label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="e.g., SUMMER24"
                  maxLength={20}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                    <option value="Scheduled">Scheduled</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">End date must be a future date</p>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCampaignModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Edit Campaign</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCampaign(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleSaveCampaign} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Code *</label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  maxLength={20}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                    <option value="Scheduled">Scheduled</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">End date must be a future date</p>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCampaign(null);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Update Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Campaign Analytics</h3>
              <button
                onClick={() => {
                  setShowAnalyticsModal(false);
                  setSelectedCampaign(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {loading ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-500">Loading analytics...</p>
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">{selectedCampaign.name}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-sm text-blue-700 mb-1">Total Usage</p>
                        <p className="text-2xl font-bold text-blue-900">{selectedCampaign.usage?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <p className="text-sm text-green-700 mb-1">Revenue Generated</p>
                        <p className="text-2xl font-bold text-green-900">{formatCurrency(selectedCampaign.revenue || 0)}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <p className="text-sm text-purple-700 mb-1">Discount</p>
                        <p className="text-2xl font-bold text-purple-900">{selectedCampaign.discount}</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <p className="text-sm text-yellow-700 mb-1">Status</p>
                        <p className="text-2xl font-bold text-yellow-900">{selectedCampaign.status}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Campaign Period</p>
                    <p className="text-gray-900 font-medium">
                      {selectedCampaign.startDate} to {selectedCampaign.endDate}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Campaign Code</p>
                    <p className="text-gray-900 font-mono font-medium">{selectedCampaign.code}</p>
                  </div>
                  {selectedCampaign.analytics?.recentUsages && selectedCampaign.analytics.recentUsages.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Recent Usage</p>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {selectedCampaign.analytics.recentUsages.map((usage, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {usage.userId?.name || 'User'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(usage.usedAt).toLocaleString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-green-600">
                                  -₹{usage.discountAmount?.toFixed(2) || 0}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Order: ₹{usage.orderAmount?.toFixed(2) || 0}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default Marketing;
